import { useId, useMemo, useState } from "react";
import { Field, Spinner } from "@cntrl/ui";
import { useMessageable } from "../../app/hooks/useInbox.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { AvMark } from "../shared/AvMark.js";
import { nameInitials } from "../../app/hooks/inboxFormat.js";
import styles from "./MessageablePicker.module.scss";

/** People with no home org (the `in_conversation` arm — someone you already talk to, with no shared band or
 *  org) bucket here. A named heading beats an unheaded straggler under the last real section. */
const OTHER_GROUP = "__other__";

export interface MessageablePickerProps {
  /** Selected person ids, owned by the form. */
  value: string[];
  onChange: (personIds: string[]) => void;
  /**
   * How many may be selected. `1` is single-select where a NEW pick SWAPS the old one (last wins) — the
   * behaviour a seat that cannot start a group needs, where refusing the second pick would read as a broken
   * control. Undefined is unbounded.
   */
  max?: number;
  /** People not to offer — the members of a chat being added to, so picking somebody already in it is impossible
   *  rather than a no-op that looks like it did something. */
  exclude?: readonly string[];
}

/**
 * THE "NEW MESSAGE" PEOPLE CHECKLIST (2026-09-13).
 *
 * A search box over a scrollable list of everyone the caller may DM, SECTIONED by the org or band each person
 * belongs to and showing each person's ROLE — the two facts the old type-ahead chip picker could not surface.
 * Both come straight off `GET /me/messageable` (`core.messageable_people`), which is the create gate read
 * forwards, so every row here is one `POST /me/threads` will accept.
 *
 * **The list is the authority for who is offered, and it is the SAME predicate the create paths enforce** —
 * building a checklist beside the gate is the ART-122 defect where the offered set and the accepted set
 * disagreed in both directions.
 *
 * **Sectioned by HOME, not by relationship.** The server hands each person their own band or management org
 * (`homeName`), which is what a reader groups people by — never `viaBandName`, which for a manager is one of
 * YOUR bands. A single group renders flat with no heading: a lone header names nothing the list does not.
 */
export function MessageablePicker({ value, onChange, max, exclude }: MessageablePickerProps) {
  const network = useMessageable();
  const [query, setQuery] = useState("");

  const people = useMemo(() => {
    const all = network.data?.people ?? [];
    if (!exclude?.length) return all;
    const out = new Set(exclude);
    return all.filter((p) => !out.has(p.personId as string));
  }, [network.data, exclude]);
  const term = query.trim().toLowerCase();

  const filtered = useMemo(
    () =>
      term
        ? people.filter((p) =>
            [p.displayName, p.roleLabel, p.homeName].some((s) => (s ?? "").toLowerCase().includes(term)),
          )
        : people,
    [people, term],
  );

  // Group by home org/band. YOUR OWN org comes first, then other orgs alphabetically, then the homeless
  // "Other" bucket. A group is the caller's own exactly when it holds a `teammate` row — that arm is another
  // seat on a band the caller sits on, so its home IS the caller's own band.
  const groups = useMemo(() => {
    const map = new Map<string, { label: string; rows: typeof filtered }>();
    for (const p of filtered) {
      const key = (p.homeId as string | null) ?? OTHER_GROUP;
      const entry = map.get(key) ?? { label: p.homeName ?? "Other", rows: [] };
      entry.rows.push(p);
      map.set(key, entry);
    }
    const rank = (key: string, g: { rows: typeof filtered }) =>
      key === OTHER_GROUP ? 2 : g.rows.some((r) => r.reason === "teammate") ? 0 : 1;
    return [...map.entries()].sort(([ak, a], [bk, b]) => {
      const d = rank(ak, a) - rank(bk, b);
      return d !== 0 ? d : a.label.localeCompare(b.label);
    });
  }, [filtered]);

  // "If only one org, don't section" — a single heading names nothing the flat list does not already say.
  const sectioned = groups.length > 1;

  function toggle(id: string) {
    if (value.includes(id)) return onChange(value.filter((v) => v !== id));
    if (max === 1) return onChange([id]); // last pick wins — a swap, not a refusal.
    onChange([...value, id]);
  }

  return (
    <div className={styles.picker}>
      <Field
        type="search"
        label="Search your network"
        hideLabel
        value={query}
        placeholder="Search your network…"
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className={styles.list}>
        {network.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={18} label="Loading people…" />
          </div>
        ) : filtered.length === 0 ? (
          <p className={styles.empty}>
            {people.length === 0 ? "No one to message yet." : "No matches."}
          </p>
        ) : (
          groups.map(([key, group]) => (
            <Section key={key} label={group.label} sectioned={sectioned}>
              {group.rows.map((p) => {
                const id = p.personId as string;
                const checked = value.includes(id);
                return (
                  <label key={id} className={styles.row} data-checked={checked || undefined}>
                    <input
                      type="checkbox"
                      className={styles.box}
                      checked={checked}
                      // The NAME is the accessible name; the role and section are ambient context beside it.
                      aria-label={p.displayName ?? "Member"}
                      onChange={() => toggle(id)}
                    />
                    <PersonAvatar
                      personId={id}
                      avatarMediaId={(p.avatarMediaId as string | null) ?? null}
                      initials={nameInitials(p.displayName ?? "")}
                      size="sm"
                      shape="circle"
                    />
                    <span className={styles.name}>{p.displayName ?? "Member"}</span>
                    {p.roleLabel && (
                      <span className={styles.role}>
                        {p.roleLabel}
                        {/* ART-171: a full-access band member wears the crimson mark here too — the mono words
                            "BAND MEMBER" are identical for full and limited, so the mark is the only tell. */}
                        <AvMark roleKey={p.roleKey as string | null} />
                      </span>
                    )}
                  </label>
                );
              })}
            </Section>
          ))
        )}
      </div>
    </div>
  );
}

/** One org section, or a bare group of rows when the whole list is one org. `role="group"` + the heading id
 *  names the set for AT; a flat list needs neither. */
function Section({
  label,
  sectioned,
  children,
}: {
  label: string;
  sectioned: boolean;
  children: React.ReactNode;
}) {
  const headId = useId();
  if (!sectioned) return <div className={styles.group}>{children}</div>;
  return (
    <div className={styles.group} role="group" aria-labelledby={headId}>
      <p id={headId} className={styles.groupHead}>
        {label}
      </p>
      {children}
    </div>
  );
}
